import { useThemeContextSelector } from "@/utils/theme";
import { useCallback } from "react";
import Box from "@mui/material/Box";

export type ToggleProps = {
    active?: boolean;
    onChange: (active: boolean) => void;
    size?: "small" | "large";
};

export const Toggle = ({ onChange, active = false, size = "large" }: ToggleProps) => {
    const isDarkMode = useThemeContextSelector(state => state.isDarkMode);

    const state = active ? "checked" : "unchecked";
    const value = active ? "on" : "off";

    const buttonStyle = {
        display: "inline-flex",
        cursor: "pointer",
        alignItems: "center",
        borderRadius: "9999px",
        border: "transparent",
        transition: "background-color 0.3s",
        backgroundColor: active ? (isDarkMode ? "#FFFFFF" : "#000000") : isDarkMode ? "#262626" : "#E5E5E5",
        "&:hover": {
            backgroundColor: active ? (isDarkMode ? "#FFFFFF" : "#000000") : isDarkMode ? "#404040" : "#D4D4D4",
        },
        ...(size === "small" && {
            height: "12px",
            width: "24px",
            padding: "2px",
        }),
        ...(size === "large" && {
            height: "20px",
            width: "36px",
            padding: "2px",
        }),
    };

    const pinStyle = {
        borderRadius: "9999px",
        pointerEvents: "none",
        display: "block",
        transition: "transform 0.3s",
        backgroundColor: isDarkMode ? "#000000" : "#FFFFFF",
        ...(size === "small" && {
            height: "8px",
            width: "8px",
        }),
        ...(size === "large" && {
            height: "16px",
            width: "16px",
        }),
        transform: active ? (size === "small" ? "translateX(12px)" : "translateX(16px)") : "translateX(0)",
    };

    const handleChange = useCallback(() => {
        onChange(!active);
    }, [active, onChange]);

    return (
        <Box
            component="button"
            sx={buttonStyle}
            type="button"
            role="switch"
            aria-checked={active}
            data-state={state}
            value={value}
            onClick={handleChange}>
            <Box component={"span"} sx={pinStyle} data-state={state} />
        </Box>
    );
};
